import React, { ReactElement, FunctionComponent, memo, useCallback, useRef } from 'react'
import AnimatedGrid from '@sport1/react-elements/AnimatedGrid'
import DimensionsProvider from '@sport1/react-elements/DimensionsProvider'
import Grid from '@sport1/react-elements/Grid'
import PanGesture from '@sport1/react-elements/PanGesture'
import useAnimation from '@sport1/react-elements/useAnimation'
import { TestID } from '../../../utils/PropTypesDefinitions'

export type VideoIndicatorProps = {
    /** boolean to determine if different progressPercentages lead to animation */
    enableIndicatorProgress?: boolean
    /** the progress of the video in percent */
    progressPercent: number
    /** the buffering of the video in percent */
    bufferingPercent: number
    /** callback when the dragging of the knob ends
     *
     * @TJS-type function
     * */
    onSeekEnd: (progress: number) => void
    /** callback when the dragging of the knob starts
     *
     * @TJS-type function
     * */
    onSeekStart?: () => void
    /** the duration of the video */
    duration: number
} & TestID

const commonBarElementHeight = 2
const VIDEO_KNOB_SIZE = 12
const yPosition = 0

const VideoIndicator: FunctionComponent<VideoIndicatorProps> = ({
    enableIndicatorProgress,
    progressPercent,
    bufferingPercent,
    onSeekEnd,
    onSeekStart,
    duration,
    testID,
}): ReactElement => {
    const panActive = useRef(false)
    const [translateX, interpolateX, startAnimationX] = useAnimation(0)
    const [translateY, , startAnimationY] = useAnimation(yPosition)

    const onPanStart = useCallback((): void => {
        panActive.current = true
        if (onSeekStart) {
            onSeekStart()
        }
    }, [onSeekStart])

    const shouldSeek = useCallback(
        (seekPositionX: number, width: number): void => {
            const seekTime = (seekPositionX * duration) / width
            onSeekEnd(seekTime)
            panActive.current = false
        },
        [duration, onSeekEnd]
    )
    const rangedProgressPercent =
        progressPercent > 0 && progressPercent <= 100 ? progressPercent : 0

    return (
        <Grid centerVertical minHeight={40} noExpand horizontal testID={testID} position="relative">
            <Grid
                noExpand
                height={commonBarElementHeight}
                backgroundColor="tahitian-4"
                width={`${bufferingPercent}%`}
                position="absolute"
                zIndex={2}
            />
            <Grid
                centerVertical
                width="100%"
                top={0}
                left={0}
                bottom={0}
                position="absolute"
                zIndex={3}
            >
                <Grid
                    noExpand
                    width={`${rangedProgressPercent}%`}
                    backgroundColor="ORANGE"
                    height={commonBarElementHeight}
                />
            </Grid>

            <Grid horizontal height={commonBarElementHeight} backgroundColor="keshi-3" />
            <Grid
                centerVertical
                top={0}
                bottom={0}
                left={0}
                right={0}
                position="absolute"
                zIndex={4}
            >
                <DimensionsProvider
                    renderChildren={(dim): ReactElement | null => {
                        if (dim?.width) {
                            const { width } = dim
                            return (
                                <PanGesture
                                    position={
                                        !panActive.current && enableIndicatorProgress
                                            ? {
                                                  x: width * (rangedProgressPercent / 100),
                                                  y: yPosition,
                                              }
                                            : undefined
                                    }
                                    translate={{ translateX, translateY }}
                                    usePanStartPosition
                                    onPanStart={onPanStart}
                                    onPanEnd={(position): void => {
                                        shouldSeek(position.x, width)
                                    }}
                                    startAnimation={{ startAnimationX, startAnimationY }}
                                >
                                    <AnimatedGrid
                                        noExpand
                                        translate={{
                                            translateX: interpolateX(
                                                [0, width],
                                                [0, width - VIDEO_KNOB_SIZE],
                                                'clamp'
                                            ),
                                            translateY: yPosition,
                                        }}
                                    >
                                        <Grid
                                            //This grid is needed to center the knob on web
                                            centerVertical
                                            position="absolute"
                                            horizontal
                                            noExpand
                                            width={40}
                                            height={40}
                                            zIndex={5}
                                        >
                                            <Grid
                                                noExpand
                                                height={VIDEO_KNOB_SIZE}
                                                width={VIDEO_KNOB_SIZE}
                                                borderRadius={VIDEO_KNOB_SIZE / 2}
                                                backgroundColor="ORANGE"
                                            />
                                        </Grid>
                                    </AnimatedGrid>
                                </PanGesture>
                            )
                        }
                        return null
                    }}
                />
            </Grid>
        </Grid>
    )
}

export default memo(VideoIndicator)
